import styled from 'styled-components';

import { gql } from '@apollo/client';
import { NextPage } from 'next';
import { ContactForm } from '../components/contact/contactForm';
import { useCookieConsent } from '../components/cookie-consent';
import { FOOTER_QUERY } from '../components/footer';
import { Footer } from '../components/footer';
import { Header, HEADER_QUERY } from '../components/header';
import { Col, ResponsiveContainer, Row } from '../components/responsive';
import { ContentStackClient } from '../lib/contentstack';
import { Contact } from '../types/contentstack';
import { IPageProps } from '../types/page';
import { mediaScreen } from '../utils/mediaScreen';
import { useRouter } from 'next/router';

export const Page: NextPage<IPageProps<Contact>> = ({
  header,
  page,
  footer,
}) => {
  const router = useRouter();
  const sales = 'sales' in router.query;
  const { isReady, hasConsent, accept } = useCookieConsent();

  return (
    <>
      <Header
        pageTitle={sales ? page?.hero?.title_sales : page?.title}
        pageDescription={page?.description}
        {...header}
      />

      <ResponsiveContainer>
        <Row>
          <Content md={12}>
            <Title>{sales ? page?.hero?.title_sales : page?.hero?.title}</Title>
            <Text>{sales ? page?.hero?.text_sales : page?.hero?.text}</Text>

            {isReady && (
              <>
                {hasConsent ? (
                  <ContactForm sales={sales} />
                ) : (
                  <ConsentNotice>
                    <ConsentText>
                      To load the contact form, please accept cookies and
                      tracking scripts.
                    </ConsentText>
                    <ConsentButton onClick={accept}>
                      Accept &amp; Load Form
                    </ConsentButton>
                  </ConsentNotice>
                )}
              </>
            )}
          </Content>
        </Row>
      </ResponsiveContainer>

      <Footer sections={footer?.sections} />
    </>
  );
};

export async function getServerSideProps() {
  try {
    const { data } = await ContentStackClient().query<IPageProps<Contact>>({
      query: PAGE_QUERY,
    });

    return { props: data };
  } catch (e) {
    return { props: {} };
  }
}

const PAGE_QUERY = gql`
  query contactQuery {
    ${HEADER_QUERY}
    ${FOOTER_QUERY}

    page: contact(uid: "bltb5b24f1ca45dad2f") {
      title
      description
      hero {
        title
        text
        title_sales
        text_sales
      }
    }
  }
`;

const Content = styled(Col)`
  align-items: center;
  display: flex;
  flex-direction: column;

  ${mediaScreen('xs', 'md')} {
    align-items: flex-start;
  }
`;

const Title = styled.h1`
  font-size: 67px;
  color: white;
  font-weight: bold;
  margin-bottom: 0;
  margin-top: 150px;
  text-align: center;

  ${mediaScreen('xs', 'md')} {
    font-size: 35px;
    text-align: left;
    margin-bottom: 24px;
  }
`;

const Text = styled.p`
  color: white;
  font-size: 22px;
  line-height: 32px;
  margin: 0 auto;
  margin-bottom: 64px;
  text-align: center;
  max-width: 630px;

  ${mediaScreen('xs', 'md')} {
    font-size: 17px;
    margin: 0;
    margin-bottom: 64px;
    text-align: left;
  }
`;

const ConsentNotice = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 16px;
  padding: 32px;
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 8px;
  max-width: 450px;
  width: 100%;
`;

const ConsentText = styled.p`
  color: white;
  font-size: 16px;
  line-height: 24px;
  text-align: center;
  margin: 0;
`;

const ConsentButton = styled.button`
  padding: 12px 24px;
  border: none;
  border-radius: 4px;
  background: #6c63ff;
  color: white;
  font-size: 16px;
  font-weight: bold;
  cursor: pointer;

  &:hover {
    background: #5a52d5;
  }
`;

export default Page;
